import React, {useEffect, useMemo, useState} from "react";
import {
  IProductListFilterItem,
  ProductListFilterItemRange,
  ProductListFilterItemRangeValue,
} from "../../../typescript-library";
import {RemoveFilterButton} from "../../atoms/RemoveFilterButton";
import RadioButton from "../../lib/form/RadioButton/RadioButton";
import {useTranslation} from "react-i18next";
import {useMediaQuery} from "react-responsive";
import i18n from "i18next";
import {percentFormat} from "../../../helpers/intlFormatters";
import {SelectOptionType} from "../../molecules/Select";
import {useProductListContext} from "../productListProvider/ProductListProvider";

type ProductFilterRangeAsSelectProps = {
  item: ProductListFilterItemRange;
  onSelect?: (values: ProductListFilterItemRangeValue | undefined) => void;
};

type ExtendedSelectOptionType = {count: number} & SelectOptionType;

const defaultCountOfVisibleItems = 7;

const ProductFilterRangeAsSelect: React.FC<ProductFilterRangeAsSelectProps> = ({item, onSelect}) => {
  const {state, dispatch} = useProductListContext();
  const {t} = useTranslation();
  const [selectedOption, setSelectedOption] = useState<ProductListFilterItemRangeValue>();
  const isMobile = useMediaQuery({query: "(max-width: 940px)"});
  const [limit, setLimit] = useState<ProductListFilterItemRangeValue>({min: 0, max: 0});
  const [open, setOpen] = useState(false);
  const [countOfVisibleItems, setCountOfVisibleItems] = useState(defaultCountOfVisibleItems);
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [isOptionsSelectedWhileLoading, setIsOptionsSelectedWhileLoading] = useState(false);

  // Range limit mechanic
  useEffect(() => {
    const minMaxValue = state.rangeFilterMap?.get(item.field);
    if (minMaxValue) {
      setLimit(minMaxValue);
    }
  }, [state.rangeFilterMap?.get(item.field)]);

  useEffect(() => {
    const filterValue = state.filters[item.field] as ProductListFilterItemRangeValue | undefined;
    setSelectedOption(filterValue ? {...filterValue} : undefined);
  }, [state.filters[item.field]]);

  useEffect(() => {
    if (!state.loading) {
      setLocalLoading(false);
    }
  }, [localLoading, state.loading]);

  useEffect(() => {
    // when former loading was finished, and in the meantime there was another option pressed, dispatch that one
    if (!localLoading) {
      if (isOptionsSelectedWhileLoading) {
        const oldValue = state.filters[item.field] as ProductListFilterItemRangeValue | undefined;

        if (oldValue === undefined && selectedOption === undefined) {
          return;
        }
        if (oldValue?.min === selectedOption?.min) {
          return;
        }
        if (!selectedOption) {
          dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
        } else {
          dispatch({
            type: "SET_FILTER",
            payload: {field: item.field, value: selectedOption},
          });
        }

        setIsOptionsSelectedWhileLoading(false);
        setLocalLoading(true);
      }
    }
  }, [localLoading]);

  const filterOptions = useMemo(() => makeDiscountArray(item), []);

  if (!filterOptions.length) {
    return null;
  }

  if (limit.min === limit.max) {
    return null;
  }

  const titleWithCountBubble = titleWithCount(
    t("general.discount"),
    selectedOption
      ? (filterOptions.find((fo: ExtendedSelectOptionType) => fo.value === selectedOption.min)?.count ?? 0)
      : filterOptions[0].count,
  );

  const onChange = (value: number) => {
    let newSelectedOption: ProductListFilterItemRangeValue | undefined;
    // if not set before, or value changed, set as new
    // if same, set as undefined to do a toggle
    if (!selectedOption || selectedOption.min !== value) {
      newSelectedOption = {min: value, max: limit.max};
    }

    setSelectedOption(newSelectedOption);

    if (onSelect) {
      onSelect(newSelectedOption);
      // Let `ProductListFilter.tsx` handle the dispatching/loading

      return;
    }

    if (localLoading) {
      setIsOptionsSelectedWhileLoading(true);
      // If former setting currently loading, let useEffect handle this setting afterward

      return;
    }

    if (!newSelectedOption) {
      dispatch({type: "REMOVE_FILTER", payload: {field: item.field}});
    } else {
      dispatch({type: "SET_FILTER", payload: {field: item.field, value: newSelectedOption}});
    }

    setLocalLoading(true);
  };

  return (
    <div className="filter-item">
      <button
        aria-expanded={open}
        className="filter-item__button"
        onClick={() => {
          setOpen(!open);
          setCountOfVisibleItems(defaultCountOfVisibleItems);
        }}
      >
        <span className="visually-hidden">{t("product.filterBy")}</span>
        <span>{titleWithCountBubble}</span>
        <i className={`icon icon-size--16 icon-chevron-${open ? "up" : "down"}`} />
      </button>
      {!!selectedOption && isMobile && (
        <div className="filter-remove__wrapper">
          <RemoveFilterButton
            onCancel={() => {
              onChange(selectedOption.min);
            }}
          >
            {`${t("general.discount")} ${selectedOption?.min} %`}
          </RemoveFilterButton>
        </div>
      )}
      {open && (
        <div className="filter-item__content">
          {!!selectedOption && !isMobile && (
            <div className="filter-remove__wrapper py-1">
              <RemoveFilterButton
                onCancel={() => {
                  onChange(selectedOption.min);
                }}
              >
                {t("general.removeDiscount")}
              </RemoveFilterButton>
            </div>
          )}
          {filterOptions.map((option, index) => {
            if (index > countOfVisibleItems - 1) {
              return null;
            }

            return (
              <RadioButton
                key={option.value}
                value={option.value}
                wrapperClassName={"py-2"}
                checked={selectedOption?.min === option.value}
                name={"percentageDiscount"}
                onChange={() => {
                  if (typeof option.value === "undefined") {
                    throw new Error("option value undefined");
                  }
                  onChange(option.value as number);
                }}
              >
                {option.text}
              </RadioButton>
            );
          })}
          {filterOptions.length > countOfVisibleItems && (
            <div
              className="filter-item__toggle-btn"
              onClick={() => {
                setCountOfVisibleItems(filterOptions.length);
              }}
            >
              {t("product.showAll")}
              <i className="icon icon-down--blue icon-size--16 ml-3" />
            </div>
          )}
          {filterOptions.length > defaultCountOfVisibleItems && countOfVisibleItems > defaultCountOfVisibleItems && (
            <div
              className="filter-item__toggle-btn"
              onClick={() => {
                setCountOfVisibleItems(defaultCountOfVisibleItems);
              }}
            >
              {t("product.showLess")}
              <i className="icon icon-top--blue icon-size--16 ml-3" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductFilterRangeAsSelect;

const makeDiscountArray = (filterItem: IProductListFilterItem): ExtendedSelectOptionType[] =>
  Array.from({length: 8})
    .map((_, index): ExtendedSelectOptionType => {
      const lowerLimit = index * 10 + 10;

      const count = Array.from(filterItem.values.values()).reduce((previousValue, currentValue) => {
        if (lowerLimit > Number(currentValue.id)) {
          return previousValue;
        }

        return previousValue + Number(currentValue.count ?? 0);
      }, 0);

      return {
        value: lowerLimit,
        count,
        text: titleWithCount(
          i18n.t("general.lowerLimit--percent", {
            percent: percentFormat(lowerLimit),
          }),
          count,
        ),
      };
    })
    .filter<ExtendedSelectOptionType>(
      (val: ExtendedSelectOptionType): val is ExtendedSelectOptionType => val?.count !== 0,
    );

const titleWithCount = (text: string, count: number) => (
  <div>
    <span>{text}</span>
    {!!count && (
      <React.Fragment>
        <span className="filter-item__count" aria-hidden>
          {"(" + count + ")"}
        </span>
        <span className="visually-hidden">{`, ${count} ${i18n.t("product.items")}`}</span>
      </React.Fragment>
    )}
  </div>
);
